// Diagnostic Engine v1 — thin API client. Every function is generic over
// `diagnosticId` so a future second diagnostic needs zero new client code.
import { getAttributionContext, getGaClientId, getPosthogDistinctId, buildAttributionRequestContext } from "@/lib/analytics";
import { getAnalyticsConsentState } from "@/hooks/useConsent";

const API = process.env.REACT_APP_BACKEND_URL;

async function request(path, options = {}) {
  const res = await fetch(`${API}/api/diagnostics${path}`, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const getDefinition = (diagnosticId, locale = "en") =>
  request(`/${diagnosticId}/definition?locale=${encodeURIComponent(locale)}`);

export const startRun = (diagnosticId, attribution) =>
  request(`/${diagnosticId}/runs`, { method: "POST", body: JSON.stringify(attribution) });

export const getRun = (runId) => request(`/runs/${runId}`);

export const putAnswer = (runId, questionId, optionId, freeText) =>
  request(`/runs/${runId}/answers/${questionId}`, {
    method: "PUT",
    body: JSON.stringify({ option_id: optionId, free_text: freeText ?? null }),
  });

// Results Page final "book a consultation?" question — answered AFTER
// completion, so it is its own call rather than part of completeRun.
export const setConsultationInterest = (runId, answer) =>
  request(`/runs/${runId}/consultation-interest`, {
    method: "POST",
    body: JSON.stringify({ answer }),
  });

/**
 * Attribution refresh fields sent at completion — ONLY the 3 that can
 * become available AFTER start_run (e.g. analytics consent was granted
 * mid-flow): ga_client_id, gclid, posthog_distinct_id. The backend
 * merges these as gap-fillers only; it never lets a value here
 * overwrite one already captured at start_run.
 *
 * `analytics_consent` (added 2026-10-09, pass 3) is the SEPARATE,
 * EXPLICIT analytics-consent decision (`getAnalyticsConsentState()`)
 * the backend records as consent_events's own `analytics` row — `null`
 * when the visitor has not yet decided, never fabricated here.
 */
/**
 * `ctaNavContext`/`runId` (added 2026-10-10, InterAct Tracking
 * Foundation milestone) — the shared browser-to-backend attribution
 * context (stable context_id + first/session/last-non-direct/
 * conversion touches), carried onward via `buildAttributionRequestContext()`
 * as `attribution_context`. `runId` here is this run's OWN id (not a
 * separately-carried one) — it is already the URL path param too; it
 * is sent inside `attribution_context` purely so the backend's shared
 * `AttributionRequestContext` shape can treat all 4 channels uniformly.
 */
export const completeRun = async (runId, { email, firstName, role, otherRoleText, company, marketingConsent, ctaNavContext, runId: contextRunId }) => {
  const gaClientId = await getGaClientId();
  return request(`/runs/${runId}/complete`, {
    method: "POST",
    body: JSON.stringify({
      email,
      first_name: firstName ?? null,
      role: role ?? null,
      other_role_text: otherRoleText || null,
      company: company || null,
      marketing_consent: !!marketingConsent,
      analytics_consent: getAnalyticsConsentState(),
      ga_client_id: gaClientId || undefined,
      gclid: new URLSearchParams(window.location.search).get("gclid") || getAttributionContext()?.gclid || undefined,
      posthog_distinct_id: getPosthogDistinctId() || undefined,
      attribution_context: buildAttributionRequestContext({ ctaNavContext, runId: contextRunId }),
    }),
  });
};

/**
 * Attribution captured at the moment the diagnostic STARTS. Falls back
 * to the persisted first-touch snapshot (`getAttributionContext()`,
 * captured unconditionally on landing — see public/index.html) whenever
 * the current URL itself carries no fresh utm/gclid params, so
 * attribution survives a visitor landing on one page, browsing, and
 * only opening the diagnostic later from a different page/session —
 * exactly the gap the current-URL-only version used to have.
 */
export const currentUrlAttribution = async () => {
  if (typeof window === "undefined") return {};
  const params = new URLSearchParams(window.location.search);
  const stored = getAttributionContext();
  const gaClientId = await getGaClientId();
  return {
    utm_source: params.get("utm_source") || stored?.utm_source || undefined,
    utm_medium: params.get("utm_medium") || stored?.utm_medium || undefined,
    utm_campaign: params.get("utm_campaign") || stored?.utm_campaign || undefined,
    utm_content: params.get("utm_content") || stored?.utm_content || undefined,
    utm_term: params.get("utm_term") || stored?.utm_term || undefined,
    gclid: params.get("gclid") || stored?.gclid || undefined,
    oppref: params.get("oppref") || stored?.oppref || undefined,
    ga_client_id: gaClientId || undefined,
    posthog_distinct_id: getPosthogDistinctId() || undefined,
    landing_page: window.location.pathname,
    referrer: document.referrer || stored?.referrer || undefined,
  };
};
